import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Clock, Calendar, List } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import FavoriteButton from '../favorites/FavoriteButton';
import AdminPdfDownloadButton from '../articles/AdminPdfDownloadButton';

// Flattens a TOC tree (sections with optional `subs`) into a single ordered list of ids,
// used to drive the scrollspy IntersectionObserver.
const flattenTocIds = (toc) => {
  const ids = [];
  toc.forEach((item) => {
    ids.push(item.id);
    (item.subs || []).forEach((sub) => ids.push(sub.id));
  });
  return ids;
};

// Tracks which heading is currently nearest the top of the viewport so the matching
// TOC entry can be highlighted (scrollspy). rootMargin biases toward the top of the
// page (just below the sticky header) rather than the true viewport center.
const useScrollSpy = (ids) => {
  const [activeId, setActiveId] = useState(ids[0]);

  useEffect(() => {
    const elements = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (elements.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          const topMost = visible.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
          setActiveId(topMost.target.id);
        }
      },
      { rootMargin: '-100px 0px -70% 0px', threshold: 0 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(',')]);

  return activeId;
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const TocLink = ({ id, label, active, sub, onNavigate }) => (
  <a
    href={`#${id}`}
    onClick={(e) => {
      e.preventDefault();
      scrollToId(id);
      if (onNavigate) onNavigate();
    }}
    data-testid={`toc-link-${id}`}
    className={`block py-1.5 leading-snug border-l-2 transition-colors ${sub ? 'pl-7 text-xs' : 'pl-4 text-sm font-medium'} ${
      active
        ? 'border-autoxium-blue text-autoxium-blue'
        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-structure'
    }`}
  >
    {label}
  </a>
);

const TocList = ({ toc, activeId, onNavigate }) => (
  <>
    {toc.map((item) => (
      <div key={item.id}>
        <TocLink id={item.id} label={item.label} active={activeId === item.id} onNavigate={onNavigate} />
        {(item.subs || []).map((sub) => (
          <TocLink key={sub.id} id={sub.id} label={sub.label} active={activeId === sub.id} sub onNavigate={onNavigate} />
        ))}
      </div>
    ))}
  </>
);

// Shell for long-form technical articles: dark hero,
// then a two-column reading view — a sticky/"frozen" left TOC (scrollspy-highlighted) and a
// right content column that scrolls normally with the page. On screens below `lg` the TOC
// collapses into a top accordion instead of a fixed side-by-side layout.
// `onDownloadPdf` (optional) shows the admin-only PDF button; it is hidden for everyone else.
export const ArticleLayout = ({ category, title, subtitle, description, readingTime, date, coverImage, toc, favorite, onDownloadPdf, backTo = '/articles', backLabel = 'Back to Articles', children }) => {
  const tocIds = useRef(flattenTocIds(toc)).current;
  const activeId = useScrollSpy(tocIds);

  return (
    <div className="min-h-screen bg-white" data-testid="hvil-article-page">
      {/* Header */}
      <div className="bg-chassis text-white">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <Link
            to={backTo}
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm mb-6"
            data-testid="back-to-articles"
          >
            <ArrowLeft className="h-4 w-4" />
            {backLabel}
          </Link>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="max-w-3xl">
              {category && (
                <Badge className="mb-4 font-mono text-xs uppercase tracking-wider bg-autoxium-blue/20 text-white border-0">
                  {category}
                </Badge>
              )}
              <h1 className="font-chivo text-3xl md:text-5xl font-black uppercase tracking-tight mb-4" data-testid="article-title">
                {title}
              </h1>
              {subtitle && <p className="text-white/70 text-lg mb-6">{subtitle}</p>}
              {description && <p className="text-white/50 text-sm mb-6 max-w-2xl">{description}</p>}
              <div className="flex flex-wrap items-center gap-6 text-sm text-white/60 font-mono">
                {readingTime && (
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {readingTime}
                  </span>
                )}
                {date && (
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {date}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <AdminPdfDownloadButton onDownload={onDownloadPdf} title={title} />
              {favorite && (
                <FavoriteButton
                  contentType={favorite.contentType}
                  contentId={favorite.contentId}
                  initialFavorited={favorite.initialFavorited}
                  title={title}
                  className="bg-white/10 hover:bg-white/20"
                  iconClassName="text-white/80"
                />
              )}
            </div>
          </div>

          {coverImage && (
            <div className="mt-8 rounded-md overflow-hidden border border-white/10" data-testid="article-cover-image">
              <img src={coverImage} alt="" className="w-full max-h-96 object-cover" />
            </div>
          )}
        </div>
      </div>

      {/* Mobile TOC (collapsible, replaces the fixed sidebar below `lg`) */}
      <div className="lg:hidden border-b border-structure bg-aluminum">
        <div className="max-w-screen-2xl mx-auto px-4">
          <Accordion type="single" collapsible>
            <AccordionItem value="toc" className="border-none">
              <AccordionTrigger className="hover:no-underline py-3" data-testid="mobile-toc-toggle">
                <span className="flex items-center gap-2 font-chivo text-sm font-bold uppercase tracking-wider text-chassis">
                  <List className="h-4 w-4 text-autoxium-blue" />
                  Table of Contents
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <nav className="pb-2" data-testid="mobile-toc-nav">
                  <TocList toc={toc} activeId={activeId} />
                </nav>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Two-column reading view */}
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="lg:grid lg:grid-cols-12 lg:gap-10 xl:gap-14">
          {/* Frozen / sticky TOC */}
          <aside className="hidden lg:block lg:col-span-3">
            <nav
              className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto pr-4 border-r border-structure"
              data-testid="desktop-toc-nav"
            >
              <p className="flex items-center gap-2 font-chivo text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                <BookOpen className="h-3.5 w-3.5 text-autoxium-blue" />
                On This Page
              </p>
              <TocList toc={toc} activeId={activeId} />
            </nav>
          </aside>

          {/* Scrolling content */}
          <article className="lg:col-span-9 min-w-0 prose-autoxium" data-testid="article-content">
            {children}
            <div className="pt-8 mt-12 border-t border-structure flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <Link
                to={backTo}
                className="inline-flex items-center gap-2 text-autoxium-blue font-mono text-sm uppercase tracking-wider hover:underline"
                data-testid="back-to-articles-bottom"
              >
                <ArrowLeft className="h-4 w-4" />
                {backLabel}
              </Link>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
};

// Top-level numbered section (1, 2, 3, ...). `id` must match the corresponding TOC entry.
export const ArticleSection = ({ id, number, title, children }) => (
  <section id={id} className="mb-12 scroll-mt-24" data-testid={`article-${id}`}>
    <h2 className="font-chivo text-2xl md:text-3xl font-black uppercase tracking-tight mb-5 text-chassis">
      <span className="text-autoxium-blue">{number}.</span> {title}
    </h2>
    <div className="space-y-4">{children}</div>
  </section>
);

// Numbered sub-section (5.1, 6.2, ...) inside an ArticleSection. `id` must match its TOC entry.
export const ArticleSubSection = ({ id, number, title, children }) => (
  <div id={id} className="mb-6 scroll-mt-24" data-testid={`article-${id}`}>
    <h3 className="font-chivo text-lg font-bold uppercase tracking-wide mb-3 text-autoxium-blue">
      {number ? `${number} ` : ''}{title}
    </h3>
    <div className="space-y-3">{children}</div>
  </div>
);

// Callout box for "Did You Know?" / "Engineering Tip" style asides from the source document.
export const Callout = ({ title, children, tone = 'info' }) => {
  const tones = {
    info: 'border-autoxium-blue/40 bg-autoxium-blue/5',
    tip: 'border-autoxium-blue/40 bg-aluminum',
  };
  return (
    <div className={`rounded-md border-l-4 p-5 my-6 ${tones[tone] || tones.info}`} data-testid="article-callout">
      {title && (
        <p className="font-chivo text-xs font-bold uppercase tracking-widest text-autoxium-blue mb-2">{title}</p>
      )}
      <div className="text-sm leading-relaxed text-foreground/90">{children}</div>
    </div>
  );
};

// Figure with caption, for embedded diagrams extracted from the source document.
// `!my-8` (not plain `my-8`) because Figure is rendered inside an ArticleSection's
// `space-y-4` wrapper, whose sibling-margin selector has higher CSS specificity than a
// bare utility class and would otherwise zero out this figure's bottom margin.
export const Figure = ({ src, alt, caption }) => (
  <figure
    className="!my-8 mx-auto max-w-full rounded-md border border-structure overflow-hidden bg-white"
    data-testid="article-figure"
  >
    <img src={src} alt={alt} className="block w-full h-auto mx-auto" loading="lazy" />
    {caption && (
      <figcaption className="px-4 py-3 text-xs text-muted-foreground text-center border-t border-structure bg-aluminum">
        {caption}
      </figcaption>
    )}
  </figure>
);

export default ArticleLayout;
